<template>
    <footer class="footer">
        <div class="container-fluid d-flex flex-wrap justify-content-between align-items-center">
            <nav>
                <ul class="footer-menu">
                    <li>
                        <a href="#" @click.prevent="showHotkeyDiscoveryModal">
                            <i class="fa fa-keyboard"></i>
                            Keyboard Shortcuts
                        </a>
                    </li>
                </ul>
            </nav>
            <div class="copyright text-right">
                &copy; {{ year }}, CP Hub LLC. <br />
                <small v-b-tooltip.hover :title="versionTexts.tooltip">{{ versionTexts.display }}</small>
            </div>
        </div>

        <HotkeyDiscoveryModal ref="hotkeyDiscoveryModal" modal-id="hotkey-discovery-modal" />
    </footer>
</template>
<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator';
import HotkeyDiscoveryModal from '@/components/Modals/HotkeyDiscoveryModal.vue';
import ClientServiceWorkerMixin from '@/mixins/ClientServiceWorkerMixin';

@Component({
    components: {
        HotkeyDiscoveryModal
    }
})
export default class ContentFooter extends Mixins(ClientServiceWorkerMixin) {
    get year() { return new Date().getFullYear(); }
    private environment: string | null = null;
    private version: string | null = null;
    // Populated from the shared worker only while the local client service is running;
    // a non-null value adds the "Client Service" segment to the footer version line.
    private clientServiceVersion: string | null = null;
    private hubConnected = false;
    private versionRetryCount = 0;

    // Displayed version (commit shortened) and tooltip (full commit hash) built together
    // so the shared reactive dependencies are evaluated once per update.
    private get versionTexts(): { display: string; tooltip: string } {
        const build = (apiVersion: string | null): string => {
            const api = [this.environment, apiVersion].filter(Boolean).join(' ');
            return [api, this.clientServiceVersion && `Client Service ${this.clientServiceVersion}`]
                .filter(Boolean).join(' · ');
        };
        return {
            display: build(this.shortCommitVersion(this.version)),
            tooltip: build(this.version),
        };
    }

    // The API version is an InformationalVersion like "1.0.0-local+<40-char git sha>".
    // Shorten the build-metadata commit (everything after "+") to a 7-char short hash.
    private shortCommitVersion(version: string | null): string | null {
        if (!version) return version;
        const plusIndex = version.indexOf('+');
        if (plusIndex === -1) return version;
        return `${version.slice(0, plusIndex)}+${version.slice(plusIndex + 1, plusIndex + 8)}`;
    }

    created() {
        document.addEventListener("keyup", this.hotKeyListener);
    }

    protected onClientServiceWorkerReady(_port: MessagePort): void {
        this.sendToWorker("clientServiceRefreshVersion");
    }

    protected onClientServiceWorkerMessage(e: MessageEvent): void {
        const [messageType, data] = e.data;
        if (messageType === "clientServiceVersion") {
            // null/empty => client service not running => line is hidden.
            this.clientServiceVersion = data || null;
            // If the hub is up but GetVersion returned null, retry up to 3 times
            // with a short delay so transient startup failures self-heal.
            if (!this.clientServiceVersion && this.hubConnected && this.versionRetryCount < 3) {
                this.versionRetryCount++;
                setTimeout(() => this.sendToWorker("clientServiceRefreshVersion"), 2000);
            }
        } else if (messageType === "clientServiceHubState") {
            if (data === "Connected") {
                this.hubConnected = true;
                this.versionRetryCount = 0;
                // Service came online — re-request the version.
                this.sendToWorker("clientServiceRefreshVersion");
            } else {
                // Closed or Reconnecting — clear the version display.
                this.hubConnected = false;
                this.clientServiceVersion = null;
            }
        }
    }

    beforeDestroy() {
        document.removeEventListener("keyup", this.hotKeyListener);
    }

    @Watch("$root.$data.dataHubFileVersion")
    onDataHubChange() {
        this.environment = this.$root.$data.dataHubFileVersion.environment;
        this.version = this.$root.$data.dataHubFileVersion.version;
    }

    hotKeyListener(e: any) {
        if (e.key === '?' || (e.shiftKey && e.key === '/')) {
            if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;

            e.preventDefault();
            this.showHotkeyDiscoveryModal();
        }
    }

    showHotkeyDiscoveryModal() {
        const modal = this.$refs.hotkeyDiscoveryModal as HotkeyDiscoveryModal;
        modal?.show();
    }
};
</script>
